import { call, put, select, takeLatest, takeEvery, all, race, take } from 'redux-saga/effects';
import * as T from './ReportsChatbotConstants';
import * as actions from './ReportsChatbotActions';
import {
    chatbotStartSessionApi,
    sendMerchantInfoApi,
    classifyQueryApi,
    metricsApi,
    insightsApi,
    chatHistoryApi,
} from './ReportsChatbotApi';
import type {
    ClassifyQuerySuccessPayload,
    ConvMessage,
    ConvMessageData,
    MetricsDataInterface,
    InsightsDataInterface,
} from '../../../interface/ReportsChatbotInterface';
import type { RootState } from '../../rootReducer';

// ── Merchant Info ─────────────────────────────────────────────────────────────

function* fetchMerchantInfoSaga(
    action: ReturnType<typeof actions.fetchMerchantInfoRequest>
) {
    try {
        const data: any = yield call(sendMerchantInfoApi, action.payload);
        yield put(actions.fetchMerchantInfoSuccess(data));
    } catch (err: any) {
        yield put(actions.fetchMerchantInfoFailure(err?.message || 'Failed to fetch merchant info'));
    }
}

// ── Session ───────────────────────────────────────────────────────────────────

function* startSessionSaga(
    action: ReturnType<typeof actions.startSessionRequest>
) {
    try {
        const data: any = yield call(chatbotStartSessionApi, action.payload);
        yield put(actions.startSessionSuccess(data));
        // Add welcome message only when conversation is empty
        const conversation: ConvMessage[] = yield select(
            (state: RootState) => state.ReportsChatbot.conversation
        );
        if (data?.message && conversation.length === 0) {
            yield put(actions.addMessage({ type: 'welcome', id: 'welcome-1', text: data.message }));
        }
    } catch (err: any) {
        yield put(actions.startSessionFailure(err?.message || 'Failed to start session'));
    }
}

// ── Chat History ──────────────────────────────────────────────────────────────

function* fetchChatHistorySaga(
    action: ReturnType<typeof actions.fetchChatHistoryRequest>
) {
    try {
        const data: any = yield call(chatHistoryApi, action.payload);
        yield put(actions.fetchChatHistorySuccess(data));
    } catch (err: any) {
        yield put(actions.fetchChatHistoryFailure(err?.message || 'Failed to fetch chat history'));
    }
}

// ── Send Query (classify → metrics? → insights) ────────────────────────────────

function* executeQueryFlow(payload: ReturnType<typeof actions.sendQueryRequest>['payload']) {
    const { query, sessionId, turnId, locationId, apiFailureResponse } = payload;

    // Classify
    yield put(actions.setClassifyLoading(true));
    let classified: ClassifyQuerySuccessPayload | null = null;
    try {
        classified = (yield call(classifyQueryApi, { session_id: sessionId, query })) as ClassifyQuerySuccessPayload;
    } catch {
        // fall through with null
    }
    yield put(actions.setClassifyLoading(false));

    if (!classified) return;

    if (classified.is_greeting && classified.greeting_reply) {
        yield put(actions.addMessage({ type: 'ai-reply', id: `${turnId}-reply`, text: classified.greeting_reply }));
        return;
    }

    if (classified.is_ambiguous && classified.clarifying_question) {
        yield put(actions.addMessage({ type: 'ai-reply', id: `${turnId}-reply`, text: classified.clarifying_question }));
        return;
    }

    const dataId = `${turnId}-data`;
    const insightsId = `${turnId}-insights`;

    if (classified.needs_data) {
        yield put(actions.addMessage({ type: 'ai-data', id: dataId, status: 'loading' } as ConvMessageData));

        yield put(actions.setMetricsLoading(true));
        let metricsData: MetricsDataInterface | null = null;
        try {
            const result: any = yield call(metricsApi, { session_id: sessionId, locationid: locationId, query });
            metricsData = Array.isArray(result) ? result[0] : result;
        } catch {
            // fall through with null
        }
        yield put(actions.setMetricsLoading(false));

        if (!metricsData) {
            yield put(actions.updateMessage({ id: dataId, changes: { status: 'skip', errorMessage: apiFailureResponse } }));
        } else {
            const newStatus = resolveDataStatus(metricsData);
            yield put(actions.updateMessage({ id: dataId, changes: { status: newStatus, data: metricsData, errorMessage: undefined } }));
        }

        yield put(actions.addMessage({ type: 'ai-insights', id: insightsId, status: 'loading' } as ConvMessage));

        yield put(actions.setInsightsLoading(true));
        const insightsData: InsightsDataInterface | null = yield* fetchInsights(sessionId, locationId, query);
        yield put(actions.setInsightsLoading(false));

        yield put(actions.updateMessage({
            id: insightsId,
            changes: {
                status: insightsData ? 'success' : 'failure',
                data: insightsData ?? undefined,
                errorMessage: insightsData ? undefined : apiFailureResponse,
            },
        }));
    } else {
        yield put(actions.addMessage({ type: 'ai-insights', id: insightsId, status: 'loading' } as ConvMessage));

        yield put(actions.setInsightsLoading(true));
        const insightsData: InsightsDataInterface | null = yield* fetchInsights(sessionId, locationId, query);
        yield put(actions.setInsightsLoading(false));

        yield put(actions.updateMessage({
            id: insightsId,
            changes: {
                status: insightsData ? 'success' : 'failure',
                data: insightsData ?? undefined,
                errorMessage: insightsData ? undefined : apiFailureResponse,
            },
        }));
    }
}

function* sendQuerySaga(
    action: ReturnType<typeof actions.sendQueryRequest>
) {
    const { query, turnId } = action.payload;

    yield put(actions.addMessage({ type: 'user', id: turnId, text: query }));

    const { wasCancelled } = yield race({
        task: call(executeQueryFlow, action.payload),
        wasCancelled: take(T.CANCEL_QUERY_REQUEST),
    });

    if (wasCancelled) {
        yield put(actions.cancelLoadingMessages());
        yield put(actions.addMessage({ type: 'ai-reply', id: `${turnId}-cancelled`, text: 'Request cancelled, retry.' }));
    }
}

// ── Retry Data (metrics + insights) ──────────────────────────────────────────

function* retryDataSaga(
    action: ReturnType<typeof actions.retryDataRequest>
) {
    const { messageId, sessionId, locationId, apiFailureResponse } = action.payload;
    const turnId = messageId.replace(/-data$/, '') || messageId;
    const insightsId = `${turnId}-insights`;

    const conversation: ConvMessage[] = yield select(
        (state: RootState) => state.ReportsChatbot.conversation
    );
    const query = getQueryForMessage(conversation, messageId);

    yield put(actions.updateMessage({ id: messageId, changes: { status: 'loading', errorMessage: undefined } }));

    yield put(actions.setMetricsLoading(true));
    let metricsData: MetricsDataInterface | null = null;
    try {
        const result: any = yield call(metricsApi, { session_id: sessionId, locationid: locationId, query });
        metricsData = Array.isArray(result) ? result[0] : result;
    } catch {
        // fall through with null
    }
    yield put(actions.setMetricsLoading(false));

    if (!metricsData) {
        yield put(actions.updateMessage({ id: messageId, changes: { status: 'skip', errorMessage: apiFailureResponse } }));
    } else {
        const newStatus = resolveDataStatus(metricsData);
        yield put(actions.updateMessage({ id: messageId, changes: { status: newStatus, data: metricsData, errorMessage: undefined } }));
    }

    // Refresh conversation to check if insights bubble already exists
    const updatedConversation: ConvMessage[] = yield select(
        (state: RootState) => state.ReportsChatbot.conversation
    );
    const insightsExists = updatedConversation.some((m) => m.id === insightsId);

    if (insightsExists) {
        yield put(actions.updateMessage({ id: insightsId, changes: { status: 'loading', errorMessage: undefined } }));
    } else {
        yield put(actions.addMessage({ type: 'ai-insights', id: insightsId, status: 'loading' } as ConvMessage));
    }

    yield put(actions.setInsightsLoading(true));
    const insightsData: InsightsDataInterface | null = yield* fetchInsights(sessionId, locationId, query);
    yield put(actions.setInsightsLoading(false));

    yield put(actions.updateMessage({
        id: insightsId,
        changes: {
            status: insightsData ? 'success' : 'failure',
            data: insightsData ?? undefined,
            errorMessage: insightsData ? undefined : apiFailureResponse,
        },
    }));
}

// ── Retry Insights only ───────────────────────────────────────────────────────

function* retryInsightsSaga(
    action: ReturnType<typeof actions.retryInsightsRequest>
) {
    const { messageId, sessionId, locationId, apiFailureResponse } = action.payload;

    const conversation: ConvMessage[] = yield select(
        (state: RootState) => state.ReportsChatbot.conversation
    );
    const query = getQueryForMessage(conversation, messageId);

    yield put(actions.updateMessage({ id: messageId, changes: { status: 'loading', errorMessage: undefined } }));

    yield put(actions.setInsightsLoading(true));
    const insightsData: InsightsDataInterface | null = yield* fetchInsights(sessionId, locationId, query);
    yield put(actions.setInsightsLoading(false));

    yield put(actions.updateMessage({
        id: messageId,
        changes: {
            status: insightsData ? 'success' : 'failure',
            data: insightsData ?? undefined,
            errorMessage: insightsData ? undefined : apiFailureResponse,
        },
    }));
}

// ── Helpers ───────────────────────────────────────────────────────────────────

function resolveDataStatus(metricsData: MetricsDataInterface): ConvMessageData['status'] {
    if (metricsData.has_metrics && metricsData.has_data) return 'metrics';
    if (!metricsData.has_metrics && metricsData.has_data) return 'detail';
    return 'skip';
}

function getQueryForMessage(conversation: ConvMessage[], messageId: string): string {
    const idx = conversation.findIndex((m) => m.id === messageId);
    for (let i = idx - 1; i >= 0; i--) {
        const m = conversation[i];
        if (m.type === 'user') return m.text;
    }
    return '';
}

function* fetchInsights(
    sessionId: string,
    locationId: string,
    query: string
): Generator<any, InsightsDataInterface | null, any> {
    try {
        const result: any = yield call(insightsApi, { session_id: sessionId, locationid: locationId, query });
        return Array.isArray(result) ? result[0] : result;
    } catch {
        return null;
    }
}

// ── Root watcher ──────────────────────────────────────────────────────────────

export default function* ReportsChatbotSaga() {
    yield all([
        takeLatest(T.FETCH_MERCHANT_INFO_REQUEST, fetchMerchantInfoSaga),
        takeLatest(T.START_SESSION_REQUEST, startSessionSaga),
        takeLatest(T.FETCH_CHAT_HISTORY_REQUEST, fetchChatHistorySaga),
        takeLatest(T.SEND_QUERY_REQUEST, sendQuerySaga),
        takeEvery(T.RETRY_DATA_REQUEST, retryDataSaga),
        takeEvery(T.RETRY_INSIGHTS_REQUEST, retryInsightsSaga),
    ]);
}
